/* eslint-disable react/no-unknown-property */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { WORKFLOWS_NS } from '../../../utils/i18n-namespaces';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { observer } from 'mobx-react-lite';
import { useSortable } from '@dnd-kit/react/sortable';
import { directionBiased } from '@dnd-kit/collision';
import { Column } from '@tanstack/react-table';
import {
  ColorPicker,
  IconButton,
  Stack,
  TextBody,
  Tooltip,
} from '@pypestream/design-system';
import { Tenancy_Node_Type_Enum_Ps_Enum } from '@pypestream/api-services';
import { useTranslation } from '@pypestream/translations';
import { NodeColumns, tableStore, TableRow } from '../../../stores/table-store';
import { shouldOpenPropertyEditorOnClick } from '../expanded-cell-view/should-open-on-click';
import { readAgentCompilerMode } from '../../../utils/agent-compiler-mode';
import { getCommonPinningStyles } from '../utils/get-common-pinning-styles';
import { groupRowCellKind } from '../utils/group-row-cells';
import {
  computeGroupHeaderLayout,
  GroupHeaderSlot,
} from '../utils/group-header-layout';
import {
  DEFAULT_GROUP_COLOR,
  GROUP_COLOR_PRESETS,
  getGroupRowTextTone,
  isCustomGroupColor,
} from './utils/group-color';
import {
  agentCategoryFor,
  agentPaletteFor,
} from '../../../utils/agent-palette';
import './draggable-row.scss';
import { InsertionLines } from './insertion-lines';
import { RowContextMenu } from './row-context-menu';
import { resolveDelayedContextMenuClose } from './row-context-menu-close';
import { resolveMenuCloseSignal } from './menu-close-signal';

interface DraggableRowProps {
  rowId: string;
  children: React.ReactNode;
  disableDrag?: boolean;
  onResizeStart: (rowId: string) => void;
  onResetSize: (rowId: string) => void;
  // PSTUDIO-2406 spike: when virtualized, the parent passes the
  // virtualizer's row index so @tanstack/react-virtual's `measureElement`
  // ResizeObserver can identify the element. Without this, the virtualizer
  // emits "Missing attribute name 'data-index={index}'" warnings and
  // re-measures the wrong elements.
  virtualIndex?: number;
  // Measured pixel width of the sticky index column. Historically the group
  // row mirrored it for its chevron-control slot; since PSTUDIO-3404 the group
  // row renders real per-column <td>s whose widths come from getCommonPinningStyles,
  // so this is no longer read here — kept on the prop for the caller's parity.
  indexCellWidth?: number;
  // PSTUDIO-3404: the row's visible TanStack columns; the group branch renders
  // one real <td> per column with true pinning styles.
  groupColumns?: Column<TableRow, unknown>[];
}

const DraggableRowComponent = (
  {
    rowId,
    children,
    onResizeStart,
    onResetSize,
    disableDrag = false,
    virtualIndex,
    groupColumns,
  }: DraggableRowProps,
  ref: React.Ref<HTMLTableRowElement>
) => {
  const [t] = useTranslation();
  const tableCellRef = useRef<HTMLTableCellElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const resizeHandleRef = useRef<HTMLDivElement>(null);

  const rowIndex = tableStore.getSortedRowIndex(rowId);

  const isRowCopiedOrCut = tableStore.isRowCopiedOrCut(rowId);
  const isRowCut = isRowCopiedOrCut && tableStore.isClipboardCut;
  const isRowSelected = tableStore.isSelectedRow(rowId);
  // PSTUDIO-2372 presence: translucent row tint when a coworker has this row
  // in their row selection.
  const coworkerRangeColorIndex =
    tableStore.coworkerRangeByRowId.get(rowId) ?? null;

  const rowSettings = tableStore.getValue(rowId, NodeColumns.settings);
  const rowNodeType = tableStore.getValue(rowId, NodeColumns.nodeType);
  const isGroupNode = rowNodeType === Tenancy_Node_Type_Enum_Ps_Enum.G;
  const subRows = rowSettings?.subRows;
  const isSubRow = !!rowSettings?.groupId;

  // PSTUDIO-3393: group row background color. Only read for group rows.
  // PSTUDIO-3393: groups are NOT "colored vs uncolored" — every group looks
  // identical (rounded header corner, guide capsule, colored closing edge); the
  // user only changes the COLOR, which defaults to the FIRST preset (a gray) when
  // none was picked. So a group with no stored color still renders the full
  // wrapper in the default gray — never a bare/flat row.
  const groupColor = isGroupNode
    ? (rowSettings?.groupColor ?? DEFAULT_GROUP_COLOR)
    : null;

  // PSTUDIO-3393: the rainbow "Custom" swatch shows its checkmark when the
  // active group color is a real custom color — i.e. set but NOT one of the 8
  // presets (case-insensitive). Pure predicate lives in group-color.ts so it is
  // unit-tested (pro-studio has no RTL); this is just the wiring.
  const isCustomColorSelected = isCustomGroupColor(
    groupColor,
    GROUP_COLOR_PRESETS
  );

  // PSTUDIO-3393: for subRows, read the parent group's color so the vertical
  // guide line can be colored to match. Only set when the parent has a color;
  // an absent var means no guide is drawn (gated in SCSS). Read via
  // tableStore.getValue so MobX tracks the dependency automatically.
  // PSTUDIO-3866: while the parent group's custom-color drag is in flight,
  // prefer the transient live preview (getGroupColorPreview) so the guide line
  // updates DURING the drag — in lock-step with the header — instead of only on
  // release. Falls back to the committed store color when no drag is active.
  const parentGroupColor = isSubRow
    ? (tableStore.getGroupColorPreview(rowSettings!.groupId!) ??
      tableStore.getValue(rowSettings!.groupId!, NodeColumns.settings)
        ?.groupColor ??
      DEFAULT_GROUP_COLOR)
    : null;

  // Get row height from store, or use currentResizeHeight if actively resizing
  const rowHeight = tableStore.getValue(rowId, NodeColumns.settings)?.rowHeight;
  const isResizing = tableStore.resizingRowId === rowId;

  const rowNodeNumber = tableStore.getValue(rowId, NodeColumns.nodeNumber);

  const agentCompilerMode = readAgentCompilerMode(tableStore.featureFlags);
  // O(1) row-level error checks via the store-side row-number index Sets
  // instead of scanning the full error list per row on every commit.
  const rowNodeNumberValue =
    typeof rowNodeNumber === 'number' ? rowNodeNumber : null;
  const hasApiError =
    agentCompilerMode !== 'off' &&
    ((rowNodeNumberValue != null &&
      tableStore.errorRowNumbers.has(rowNodeNumberValue)) ||
      // id-first fallback: a blank-node-number row still lights up by row id
      // (PSTUDIO-3991).
      tableStore.errorRowIds.has(rowId));
  const hasPifError =
    agentCompilerMode !== 'bot_only' &&
    ((rowNodeNumberValue != null &&
      tableStore.pifErrorRowNumbers.has(rowNodeNumberValue)) ||
      tableStore.pifErrorRowIds.has(rowId));
  const hasError = hasApiError || hasPifError;

  const { dragSession } = tableStore;
  const isMultiDrag = dragSession?.isMulti;

  // PSTUDIO-4229: one definition, shared with `row-context-menu.tsx` and the
  // pure `resolveRowPasteTargets` — `stores/utils/continuous-multi-selection.ts`.
  // `isRowSelected` above stays because other branches in this component read
  // it; the predicate itself is no longer restated here.
  const isPartOfMultiSelection =
    tableStore.isRowInContinuousMultiSelection(rowId);

  const shouldDisableSortable =
    isMultiDrag && dragSession?.activeRowId !== rowId;

  const { ref: sortableRef, isDragging } = useSortable({
    id: rowId,
    index: rowIndex,
    handle: dragHandleRef,
    collisionDetector: directionBiased,
    disabled: shouldDisableSortable || disableDrag,
    transition: isMultiDrag ? null : undefined,
  });

  const rowElementRef = useRef<HTMLTableRowElement | null>(null);

  const rowSortableRef =
    isMultiDrag || isPartOfMultiSelection ? undefined : sortableRef;

  const rowIntent = tableStore.getValue(rowId, NodeColumns.intent);

  const isRowHighlighted = tableStore.doesRowContainSelectedCells(rowId);

  const [contextMenuState, setContextMenuState] = useState<
    'closed' | 'closing' | 'open'
  >('closed');

  // PSTUDIO-3393: color-picker popover state.
  // `colorPickerOpen` drives the fixed-position dropdown. `customColorMode`
  // expands the HSV/hex ColorPicker inside the dropdown. `colorPickerAnchor`
  // holds the DOMRect of the palette IconButton so the portal panel can
  // position itself relative to it — a fixed-position portal is used because
  // the group row is inside a virtualized, scrolling table, where position:
  // absolute cannot escape the overflow container.
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [customColorMode, setCustomColorMode] = useState(false);
  const colorPickerBtnRef = useRef<HTMLElement | null>(null);
  const colorPickerPanelRef = useRef<HTMLDivElement | null>(null);
  const [colorPickerAnchorRect, setColorPickerAnchorRect] =
    useState<DOMRect | null>(null);
  // PSTUDIO-3393: the DS ColorPicker fires `input` on every pointermove frame
  // during an HSV/hue drag but `change` only once, on release. Committing each
  // frame to the store spams the undo stack + saveImmediately DB writes (a ~1s
  // drag = dozens of non-atomic undo commands / upserts). Mirror the row/column
  // resize snapshot-at-start/commit-on-release pattern: hold the in-flight color
  // in a transient store slot for live preview, and commit to the store once, on
  // `change`.
  // PSTUDIO-3866: this preview lives on `tableStore.groupColorPreview` (was local
  // React state) so the group's SUBROWS can read it too and paint their guide
  // lines live during the drag, not just the header. `null` = no drag in flight.
  const dragPreviewColor = isGroupNode
    ? tableStore.getGroupColorPreview(rowId)
    : null;

  // PSTUDIO-3866: the live drag-preview color must exist ONLY while the HSV
  // custom picker is actively open. `change` clears it on a normal drag release,
  // but if it leaks past close (Escape / click-outside) or past a preset pick —
  // both of which close the picker without firing the picker's `change` — the
  // header keeps painting the stale preview (`effectiveGroupColor =
  // dragPreviewColor ?? groupColor`) while the subrow guide lines read the
  // committed store color, producing a permanent header↔border desync (and the
  // "a preset changes only the borders, not the row" / "two clicks to select"
  // symptoms). Clearing the preview whenever the HSV custom picker is not
  // actively open makes the committed store `groupColor` the single source of
  // truth for BOTH the header and the guide lines outside an in-flight drag.
  useEffect(() => {
    if (!colorPickerOpen || !customColorMode) {
      tableStore.setGroupColorPreview(rowId, null);
    }
  }, [colorPickerOpen, customColorMode, rowId]);

  // PSTUDIO-3866: because the preview now lives on the store (not React state),
  // it is NOT discarded for free when this group row unmounts — unlike the old
  // `useState`. If the row virtualizes out of the window mid-drag (or is removed
  // / ungrouped) the picker portal + the close-effect above vanish without ever
  // clearing the slot, so an orphaned preview could later tint a group that
  // reuses this id. Clear our own key on unmount. The clear is a groupId-scoped
  // `delete` on the preview map, so it never wipes another group's in-flight drag.
  useEffect(() => () => tableStore.setGroupColorPreview(rowId, null), [rowId]);

  // PSTUDIO-3393: while a custom-color drag is in flight, paint the group row
  // from the local preview color (committed to the store only on release) so
  // the user keeps live feedback without churning undo/persistence. Falls back
  // to the stored `groupColor` when no drag is active.
  const effectiveGroupColor = dragPreviewColor ?? groupColor;
  const effectiveGroupTone = getGroupRowTextTone(effectiveGroupColor);
  // Build polish: a Use-case agent's band goes soft (tint + deep text + a
  // solid edge) while Supervisor/Knowledge stay solid. The vars ride along on
  // every use-case group; SCSS applies them only under `.bobcat-polish`
  // (five-stage nav on), so the classic table is untouched.
  const groupPalette = isGroupNode
    ? agentPaletteFor({
        color: effectiveGroupColor,
        category: agentCategoryFor(rowSettings?.groupName),
      })
    : null;

  // The group-row name uses the same uncontrolled pattern as the plain-text
  // cell editors (cell.tsx): the DOM input owns its value while editing and we
  // write to the store only on blur/Enter. A `key` derived from the saved
  // groupName remounts the field whenever the stored value changes (reload,
  // undo, collab edit) so it never shows a stale value.
  //
  // Driving `value` from React state was the bug: @lit/react re-applies `value`
  // to ps-input on every re-render, ps-input mirrors it into `internalValue`,
  // and `live()` rewrites the DOM — so the per-keystroke re-render (made worse
  // by the post-Enter saveImmediately churn) kept clobbering in-flight input.
  const commitGroupName = useCallback(
    (nextValue: string) => {
      // PSTUDIO-2154: route through setGroupName so the rename is recorded as a
      // single undo/redo command (it no-ops when the value is unchanged).
      tableStore.setGroupName(rowId, nextValue);
    },
    [rowId]
  );

  const resetSize = () => {
    onResetSize(rowId);
  };

  // Modify children to add drag handle to first cell
  const childrenWithHandles = React.Children.map(children, (child) => {
    return child;
  })?.slice(1);

  const handleIndexCellClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    tableStore.deselectAllCells();
    tableStore.isCellBeingEdited = false;

    if (contextMenuState === 'open') {
      setContextMenuState('closing');
    }

    // PSTUDIO-1433: the marker is NOT cleared here. It is owned by the
    // state-driven effect below, and having a third close path clear it
    // imperatively is exactly what left it stuck on a reopened row before
    // (PSTUDIO-3162 family) — one owner, one moment.

    if (e.shiftKey) {
      tableStore.selectRowRange(rowIndex);
      (e.currentTarget as HTMLElement).blur();
      return;
    }

    if (
      tableStore.rowRangeSelection?.isSelecting ||
      tableStore.consumeSuppressNextRowClick()
    ) {
      return;
    }

    if (tableStore.isSelectedRow(rowId)) {
      if (!e.metaKey && !e.ctrlKey) {
        tableStore.deselectAllRows();
        tableStore.selectRow(rowId);
        tableStore.setRowSelectionAnchor(rowIndex);
      } else {
        tableStore.deSelectRow(rowId);
      }
    } else {
      if (e.metaKey || e.ctrlKey) {
        tableStore.selectRow(rowId);
        tableStore.setRowSelectionAnchor(rowIndex);
      } else {
        if (tableStore.selectedRows.size > 0) {
          tableStore.deselectAllRows();
        }
        tableStore.selectRow(rowId);
        tableStore.setRowSelectionAnchor(rowIndex);
      }
    }

    // Project Bobcat: the row-number cell opens the NODE property panel, the
    // same as clicking any other cell in the row. It needs its own call because
    // this <td> is rendered here, not by `CellComponent`, so the equivalent
    // call in `cell.tsx` never fires for column 0 — which is exactly why
    // clicking the row number was the one gesture that did nothing.
    //
    // LAST, after the branches above have settled the selection: the store only
    // opens when the click resolved to a real node, and it reads that from
    // `selectedNodeRowId`. Calling earlier would test the PREVIOUS selection —
    // and clicking a group row would flip the persisted open flag, close an
    // open Supervisor / Resource Library panel, and show nothing.
    //
    // Shares `shouldOpenPropertyEditorOnClick` with the cell path so the two
    // gestures cannot drift; it excludes the shift/meta/ctrl multi-select
    // gestures the branches above implement. `immediate` because this cell has
    // no double-click gesture to protect (the row's only one is on the resize
    // handle, a separate sibling element), so there is nothing for the grace
    // period to guard against.
    if (shouldOpenPropertyEditorOnClick(e)) {
      tableStore.openPropertyPanelFromClick({ immediate: true });
    }
  };

  const DRAG_THRESHOLD = 2;
  const SCROLL_EDGE_SIZE = 50;
  const SCROLL_SPEED = 10;

  const handleIndexCellPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;

    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    if (isRowSelected) return;

    tableStore.suppressNextRowClick = false;

    const target = e.currentTarget as HTMLElement;
    const { pointerId, clientX: startX, clientY: startY } = e;
    const startRowIndex = rowIndex;
    let rangeStarted = false;
    let hasPointerCapture = false;
    let scrollAnimationId: number | null = null;
    let lastPointerY = startY;

    const scrollContainer = target.closest('.c-editor-table-scroller');

    const autoScroll = () => {
      if (!scrollContainer || !rangeStarted) return;

      const rect = scrollContainer.getBoundingClientRect();
      const distanceFromTop = lastPointerY - rect.top;
      const distanceFromBottom = rect.bottom - lastPointerY;

      let scrollDelta = 0;
      if (distanceFromTop < SCROLL_EDGE_SIZE && scrollContainer.scrollTop > 0) {
        scrollDelta = -SCROLL_SPEED * (1 - distanceFromTop / SCROLL_EDGE_SIZE);
      } else if (
        distanceFromBottom < SCROLL_EDGE_SIZE &&
        scrollContainer.scrollTop <
          scrollContainer.scrollHeight - scrollContainer.clientHeight
      ) {
        scrollDelta =
          SCROLL_SPEED * (1 - distanceFromBottom / SCROLL_EDGE_SIZE);
      }

      if (scrollDelta !== 0) {
        scrollContainer.scrollTop += scrollDelta;

        const elements = document.elementsFromPoint(
          startX + (lastPointerY - startY) * 0.1,
          lastPointerY
        );
        for (const element of elements) {
          const row = element.closest('tr[data-row-index]');
          if (row) {
            const rowIndexAttr = row.getAttribute('data-row-index');
            if (rowIndexAttr !== null) {
              const idx = parseInt(rowIndexAttr, 10);
              if (!isNaN(idx)) {
                tableStore.updateRowRangeSelection(idx);
                break;
              }
            }
          }
        }
      }

      scrollAnimationId = requestAnimationFrame(autoScroll);
    };

    const handleMove = (moveEvent: PointerEvent) => {
      if (tableStore.dragSession) {
        cleanup();
        return;
      }

      lastPointerY = moveEvent.clientY;
      const dx = Math.abs(moveEvent.clientX - startX);
      const dy = Math.abs(moveEvent.clientY - startY);

      if (!rangeStarted && (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD)) {
        rangeStarted = true;

        if (!target.hasPointerCapture(pointerId)) {
          target.setPointerCapture(pointerId);
          hasPointerCapture = true;
        }

        moveEvent.preventDefault();
        moveEvent.stopPropagation();
        tableStore.startRowRangeSelection(startRowIndex);

        scrollAnimationId = requestAnimationFrame(autoScroll);
      }

      if (rangeStarted && tableStore.rowRangeSelection?.isSelecting) {
        moveEvent.preventDefault();
        moveEvent.stopPropagation();

        const elements = document.elementsFromPoint(
          moveEvent.clientX,
          moveEvent.clientY
        );
        for (const element of elements) {
          const row = element.closest('tr[data-row-index]');
          if (row) {
            const rowIndexAttr = row.getAttribute('data-row-index');
            if (rowIndexAttr !== null) {
              const idx = parseInt(rowIndexAttr, 10);
              if (!isNaN(idx)) {
                tableStore.updateRowRangeSelection(idx);
                break;
              }
            }
          }
        }
      }
    };

    const handleUp = () => {
      if (rangeStarted && tableStore.rowRangeSelection?.isSelecting) {
        tableStore.endRowRangeSelection();
      }
      cleanup();
    };

    const cleanup = () => {
      if (scrollAnimationId !== null) {
        cancelAnimationFrame(scrollAnimationId);
        scrollAnimationId = null;
      }
      if (hasPointerCapture && target.hasPointerCapture(pointerId)) {
        target.releasePointerCapture(pointerId);
        hasPointerCapture = false;
      }
      target.blur();
      document.removeEventListener('pointermove', handleMove, {
        capture: true,
      });
      document.removeEventListener('pointerup', handleUp, { capture: true });
      document.removeEventListener('pointercancel', handleUp, {
        capture: true,
      });
    };

    document.addEventListener('pointermove', handleMove, { capture: true });
    document.addEventListener('pointerup', handleUp, { capture: true });
    document.addEventListener('pointercancel', handleUp, { capture: true });
  };

  const cellId = `editor-table-td-index-${rowId}`;

  // PSTUDIO-1433 (crash fix): the close is DELAYED so the popover's own close
  // animation can finish — and that delay used to be able to unmount a menu that
  // had been REOPENED in the meantime. Unmounting an OPEN menu crashes the whole
  // page: `ps-dropdown` moves ITSELF into the popover portal while open
  // (`popover-base.wc.ts`, `projectedContent = this`), so React's `removeChild`
  // runs against a node whose real parent is the portal slot and throws
  // `NotFoundError: The node to be removed is not a child of this node`, which
  // drops the app into the error boundary. Measured in the browser: right-click
  // (open) → click a menu item (closing, schedules this) → right-click again
  // (open) → the stale timer fired `closed` → crash.
  //
  // Two guards, because either alone leaves a window open: the timer is
  // CANCELLED when the state moves on, and — for a timer that already fired
  // before the cleanup ran — `resolveDelayedContextMenuClose` completes only a
  // close that is still pending.
  useEffect(() => {
    if (contextMenuState !== 'closing') return undefined;

    const timeoutId = setTimeout(() => {
      setContextMenuState(resolveDelayedContextMenuClose);
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [contextMenuState]);

  // PSTUDIO-3162 family: `data-popover-open` paints the row purple while its
  // menu is up (`draggable-row.scss` `:has(.c-editor-table-td--index-cell[data-popover-open])`).
  // React does not manage the attribute, so it is cleared from the STATE rather
  // than from whichever close path happened to run: tying it to the delayed
  // close alone left it stuck on a row whose menu had been reopened and then
  // closed by another path (measured — two `<td>`s kept the marker with no open
  // dropdown anywhere on the page).
  useEffect(() => {
    if (contextMenuState === 'closed') {
      tableCellRef.current?.removeAttribute('data-popover-open');
    }
  }, [contextMenuState]);

  // PSTUDIO-1433 (crash fix, second entrance): the table is virtualized, so
  // scrolling far enough unmounts this row while its menu is still open — the
  // same portal-hosted `removeChild` crash the delayed-close guard above fixes
  // for the reopen path, reached here with no user gesture at all. Close on
  // scroll, which is what a menu should do anyway.
  const closeMenuOnScroll = useCallback(() => {
    setContextMenuState((current) =>
      current === 'closed' ? current : 'closing'
    );
  }, []);
  // React to the table-level close signal instead of subscribing to scroll per
  // cell/row — see `requestCloseAllContextMenus`. Reading the nonce inside an
  // observer component makes this a MobX subscription, which costs nothing
  // until the value actually changes.
  // Keep the store's open-menu count in step with this menu's own state, so
  // the table can subscribe to scroll only while some menu is actually open.
  const menuIsOpen = contextMenuState !== 'closed';
  useEffect(() => {
    if (!menuIsOpen) return undefined;
    tableStore.registerOpenContextMenu();
    return () => tableStore.unregisterOpenContextMenu();
  }, [menuIsOpen]);

  // Subscribe to the close signal ONLY while this menu is open — see the same
  // guard in `cell.tsx`: an unconditional read subscribed every rendered row to
  // a nonce that concerns at most one (PSTUDIO-1433).
  const closeNonce = menuIsOpen ? tableStore.contextMenuCloseNonce : 0;
  // The nonce is monotonic and never resets, so "non-zero" cannot mean "a signal
  // arrived for me" — see `resolveMenuCloseSignal`, which owns that reasoning
  // and is shared with the row menu.
  const nonceLatchRef = useRef<number | null>(null);
  useEffect(() => {
    const { latchedNonce, shouldClose } = resolveMenuCloseSignal({
      latchedNonce: nonceLatchRef.current,
      isOpen: menuIsOpen,
      nonce: closeNonce,
    });
    nonceLatchRef.current = latchedNonce;
    if (shouldClose) closeMenuOnScroll();
    // `closeMenuOnScroll` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuIsOpen, closeNonce]);

  // PSTUDIO-3393: close color-picker popover on click-outside.
  // Pointer-capture or portals inside a virtual table can lose events, so we
  // listen on the document's `pointerdown` and dismiss when the click target
  // is outside both the trigger button and the floating panel.
  useEffect(() => {
    if (!colorPickerOpen) return;

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (
        colorPickerBtnRef.current?.contains(target) ||
        colorPickerPanelRef.current?.contains(target)
      ) {
        return;
      }
      setColorPickerOpen(false);
      setCustomColorMode(false);
    };

    document.addEventListener('pointerdown', handlePointerDown, {
      capture: true,
    });
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, {
        capture: true,
      });
    };
  }, [colorPickerOpen]);

  const openColorPicker = useCallback(() => {
    if (!colorPickerBtnRef.current) return;
    setColorPickerAnchorRect(colorPickerBtnRef.current.getBoundingClientRect());
    setColorPickerOpen((prev) => !prev);
    if (colorPickerOpen) {
      setCustomColorMode(false);
    }
  }, [colorPickerOpen]);

  const openRowContextMenu = (e: React.MouseEvent) => {
    if (tableStore.isViewer) return;
    e.preventDefault();
    e.stopPropagation();
    tableStore.deselectAllCells();
    if (tableStore.selectedRows.size >= 1 && !tableStore.isSelectedRow(rowId)) {
      tableStore.deselectAllRows();
    }
    tableStore.selectRow(rowId);
    // PSTUDIO-3162 symmetry: SET the marker on the same element the close-path
    // cleanups REMOVE it from (`tableCellRef`), never on `e.currentTarget`. The
    // group row (PSTUDIO-3404) wires `onContextMenu` on EVERY cell so a
    // right-click anywhere on the band opens the menu, yet the attribute must
    // always land on the one cell that carries `tableCellRef` — otherwise it
    // goes stale on whichever cell was clicked. For the regular index-cell path
    // `e.currentTarget === tableCellRef.current`, so this is equivalent there.
    tableCellRef.current?.setAttribute('data-popover-open', '');
    setContextMenuState('open');
    // PSTUDIO-3337 / PSTUDIO-3547: proactively refresh the in-memory node bundle
    // from the SYSTEM clipboard when the menu opens so the row menu's Paste items
    // enable (canPasteRowAt) AND reflect the LATEST copy across tabs / solutions
    // ("last copy wins"). Fire-and-forget: MobX re-renders the open menu when the
    // async read lands; the paste itself re-refreshes before writing. Skipped for
    // a same-tab CUT (isClipboardCut) — hydrating would turn the move into a copy.
    if (!tableStore.isClipboardCut) {
      void tableStore.hydrateRowClipboardFromSystem();
    }
  };

  const renderIndexCell = () => {
    return (
      <td
        test-id={`c-editor-table--indexCell-td-${rowIndex}`}
        id={cellId}
        // `data-popover-open` is SET on this <td> (onContextMenu below uses
        // e.currentTarget), so the ref the close-path cleanups use to REMOVE
        // it must point at the same element. It used to sit on the inner
        // <div>, leaving a stale attribute that kept the row purple via the
        // `:has(.c-editor-table-td--index-cell[data-popover-open])` SCSS rule.
        ref={tableCellRef}
        className={`c-editor-table-td c-editor-table-td--index-cell ${hasError ? 'c-editor-table-td--error' : ''} ${rowIntent === 'out_of_scope' ? 'c-editor-table-td--out-of-scope' : ''} ${isRowHighlighted ? 'c-editor-table-td--highlighted' : ''} ${isSubRow ? 'c-editor-table-td--group' : ''}`}
        style={{
          padding: tableStore.isEditing() ? '0' : '6px 12px',
          textAlign: 'center',
          position: 'sticky',
          pointerEvents: isResizing ? 'none' : 'auto', // Disable pointer events during resize
        }}
        role="gridcell"
        tabIndex={-1}
        onContextMenu={openRowContextMenu}
      >
        <div
          test-id={`c-editor-table--indexCell-${rowIndex}-${tableStore.isEditing() ? 'edit' : 'view'}-mode-inner`}
          className="c-editor-cell__inner"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            height: '100%',
            // PSTUDIO-3663: keep the index-cell content (row number) above the
            // row-selection ::before overlay (draggable-row.scss, z-index:12).
            // At the old z-index:11 the light overlay painted over the white
            // number on a selected row, so "Go to Node" left the number blank
            // until the next click cleared the selection.
            zIndex: 13,
          }}
        >
          {tableStore.isEditing() ? (
            <div
              onClick={handleIndexCellClick}
              ref={dragHandleRef}
              className="c-editor-table-tr__drag-hadle"
              onPointerDown={handleIndexCellPointerDown}
              test-id={`c-editor-table--indexCell-${rowIndex}-drag-handle`}
            >
              <div
                className="c-editor-table-row--drag-handle"
                style={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg
                  test-id={`c-editor-table--indexCell-${rowIndex}-drag-icon`}
                  className="c-editor-table-row--drag-icon"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                >
                  <path
                    stroke="none"
                    fill="currentColor"
                    d="M15 15.984q0.797 0 1.406 0.609t0.609 1.406-0.609 1.406-1.406 0.609-1.406-0.609-0.609-1.406 0.609-1.406 1.406-0.609zM15 9.984q0.797 0 1.406 0.609t0.609 1.406-0.609 1.406-1.406 0.609-1.406-0.609-0.609-1.406 0.609-1.406 1.406-0.609zM15 8.016q-0.797 0-1.406-0.609t-0.609-1.406 0.609-1.406 1.406-0.609 1.406 0.609 0.609 1.406-0.609 1.406-1.406 0.609zM9 3.984q0.797 0 1.406 0.609t0.609 1.406-0.609 1.406-1.406 0.609-1.406-0.609-0.609-1.406 0.609-1.406 1.406-0.609zM9 9.984q0.797 0 1.406 0.609t0.609 1.406-0.609 1.406-1.406 0.609-1.406-0.609-0.609-1.406 0.609-1.406 1.406-0.609zM11.016 18q0 0.797-0.609 1.406t-1.406 0.609-1.406-0.609-0.609-1.406 0.609-1.406 1.406-0.609 1.406 0.609 0.609 1.406z"
                  ></path>
                </svg>
              </div>
              <span className="c-editor-table-row--row-number">
                {tableStore.getRowDisplayNumber(rowId)}
              </span>
            </div>
          ) : (
            <span className="c-editor-table-row--row-number">
              {tableStore.getRowDisplayNumber(rowId)}
            </span>
          )}
        </div>
        {/* {(child as React.ReactElement).props.children} */}
        {/* Drag proxy: invisible element for multi-row drag */}
        <div
          ref={isPartOfMultiSelection ? sortableRef : undefined}
          className="c-editor-table-tr__drag-proxy"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            opacity: 0,
          }}
        />
        {/* Resize handle at bottom */}
        <div
          ref={resizeHandleRef}
          test-id={`c-editor-table--indexCell-${rowIndex}-resize-handle`}
          className="c-editor-table-row-resize-handle"
          onDoubleClick={resetSize}
          onMouseDown={() => {
            onResizeStart(rowId);
          }}
        />
        {tableStore.isEditing() && contextMenuState !== 'closed' && (
          <RowContextMenu
            rowId={rowId}
            triggerId={cellId}
            rowIndex={rowIndex}
            active={contextMenuState === 'open'}
            onRequestClose={() => setContextMenuState('closing')}
          />
        )}
      </td>
    );
  };

  const rowStyles: React.CSSProperties = {};
  if (rowHeight) {
    (rowStyles as any)['--c-editor-table-tr-height'] = `${rowHeight}px`;
  }

  const classNames = [
    'c-editor-table-tr',
    isDragging && !isMultiDrag && 'c-editor-table-tr--dragging',
    isRowSelected && 'c-editor-table-tr--selected',
    isRowCopiedOrCut && 'c-editor-table-tr--copied',
    isRowCut && 'c-editor-table-tr--cut',
    hasError && 'c-editor-table-tr--error',
    isResizing && 'c-editor-table-tr--resizing',
    isMultiDrag && 'c-editor-table-tr--drag-frozen',
    isSubRow && 'c-editor-table-tr--subrow',
    coworkerRangeColorIndex != null &&
      `c-editor-table-tr--coworker-range c-editor-table-tr--coworker-range-${coworkerRangeColorIndex}`,
  ]
    .filter(Boolean)
    .join(' ');

  // PSTUDIO-2406 spike fix: stabilize the combined ref. Recreating this every
  // render caused React to call the previous ref with `null` and the new ref
  // with the element, which in turn cycled `useSortable`'s registration in
  // dnd-kit. Under drag (where dragSession observables update on every move)
  // that produced thousands of register/unregister cycles per second.
  const combinedRef = useCallback(
    (element: HTMLTableRowElement | null) => {
      rowElementRef.current = element;

      if (rowSortableRef) {
        rowSortableRef(element);
      }

      if (ref) {
        if (typeof ref === 'function') {
          ref(element);
        } else {
          (ref as React.MutableRefObject<HTMLTableRowElement | null>).current =
            element;
        }
      }
    },
    [rowSortableRef, ref]
  );

  if (isGroupNode) {
    const cols = groupColumns ?? [];
    // PSTUDIO-3865: the four header elements — the top node's number, the
    // node-count badge, the group-name input, and the action buttons — are bound
    // POSITIONALLY to the first four VISIBLE non-control columns (pinning /
    // reorder included), NOT to specific column ids. Each element renders in its
    // OWN <td> and is clipped to that column, so nothing overlaps regardless of
    // which columns are pinned/reordered/hidden. This replaces the earlier
    // absolutely-positioned "strip" + offset math, whose anchor/offset drifted
    // and let the fields collide once a column was pinned left (delegated to the
    // pure, unit-tested `computeGroupHeaderLayout` — see group-header-layout.ts).
    // The leading control cell (chevron + drag handle) stays a dedicated service
    // column and never hosts a content slot; the four slots fill the columns
    // AFTER it. When fewer than four visible content columns exist, trailing
    // slots clamp onto the last one so the action buttons are never dropped.
    const { slotsByColIndex } = computeGroupHeaderLayout(
      cols.map((c) => ({
        id: c.id,
        isControl: groupRowCellKind(c.id) === 'control',
        // @ts-expect-error - hidden is a custom columnDef field
        hidden: !!(c.columnDef.hidden as boolean | undefined),
      }))
    );
    return (
      <>
        <tr
          // Bind the dnd-kit sortable ref so the group row is registered as a
          // draggable participant. Without this the row exists in the table
          // but useSortable cannot resolve its DOM and the drag-end intercept
          // in table.tsx (Tenancy_Node_Type_Enum_Ps_Enum.G branch) never fires.
          ref={combinedRef}
          className="c-editor-table-tr c-editor-table-tr--group"
          test-id={`c-editor-table-tr-group-${rowIndex}`}
          data-rowid={rowId}
          data-row-index={rowIndex}
          // @tanstack/react-virtual's measureElement ResizeObserver keys on
          // data-index; the group row is virtualized like any other, so it must
          // carry it too or the virtualizer re-measures the wrong element.
          data-index={virtualIndex}
          // table-nav contract: the group header renders <td>s with no
          // role="gridcell" — keyboard navigation must skip this row or focus
          // silently dies here (PSTUDIO-3168).
          data-nav-skip="true"
          // PSTUDIO-3393: apply group background color + contrast tone.
          // The CSS var is consumed by `.c-editor-table-tr-group__cell` to paint
          // all cells with an opaque background (required for pinned-column masking).
          // `data-group-tone` flips text/input/badge/icons to light when the
          // background is dark.
          {...(groupColor
            ? ({
                style: {
                  '--group-bg-color': effectiveGroupColor,
                  ...(groupPalette?.soft
                    ? {
                        '--group-soft-fill': groupPalette.fill,
                        '--group-soft-text': groupPalette.text,
                        // The raw group color, so the band's edge matches
                        // the gutter guide line and the colored closing edge
                        // (both already the raw color) below it.
                        '--group-soft-edge': effectiveGroupColor,
                      }
                    : {}),
                } as React.CSSProperties,
                'data-group-tone': effectiveGroupTone,
                'data-agent-category': groupPalette?.category,
                'data-group-expanded': rowSettings?.groupExpanded
                  ? 'true'
                  : undefined,
              } as React.HTMLAttributes<HTMLTableRowElement>)
            : {})}
        >
          {/* PSTUDIO-3404: render one real <td> per visible column, each with
            its true pinning styles, so the group band lines up exactly with
            regular rows (index/nodeNumber pinned-left, hidden columns hidden).
            PSTUDIO-3865: the four header elements are bound POSITIONALLY to the
            first four visible non-control columns (see `slotsByColIndex`); each
            renders in its own cell and is clipped to that column. */}
          {cols.map((column, colIndex) => {
            const kind = groupRowCellKind(column.id);
            const pinning = getCommonPinningStyles(column);
            // `hidden` is a custom columnDef field (mirrors the regular Cell in
            // cell.tsx / table.tsx); it isn't part of TanStack's ColumnDef type.
            // @ts-expect-error - hidden is a custom columnDef field
            const hidden = column.columnDef.hidden as boolean | undefined;
            const isControl = kind === 'control';
            const slots: GroupHeaderSlot[] = slotsByColIndex[colIndex] ?? [];
            // The cell that hosts the action buttons is the LAST occupied slot;
            // its content is allowed to overflow RIGHTWARDS over the empty
            // decorative cells (nothing to collide with there — PSTUDIO-3865:
            // "the last column must not clip content that overflows right"),
            // whereas the inner slots (number/count/name) clip to their column
            // so they never overlap the next occupied element.
            const hasActions = slots.includes('actions');
            // PSTUDIO-3865: the name-input cell drops its horizontal padding so
            // the input fills 100% of the column edge-to-edge (only this cell —
            // number/count/actions keep the standard cell inset).
            const hasName = slots.includes('name');

            return (
              <td
                key={column.id}
                data-hidden={hidden}
                id={isControl ? cellId : undefined}
                ref={isControl ? tableCellRef : undefined}
                className={[
                  'c-editor-table-td',
                  'c-editor-table-td--secondary',
                  'c-editor-table-tr-group__cell',
                  `c-editor-table-tr-group__cell--${kind}`,
                  slots.length > 0
                    ? 'c-editor-table-tr-group__cell--content'
                    : '',
                  hasActions
                    ? 'c-editor-table-tr-group__cell--actions-host'
                    : '',
                  hasName ? 'c-editor-table-tr-group__cell--name-host' : '',
                  pinning.classNames,
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{
                  ...pinning.styles,
                  ...(hidden ? { display: 'none' } : {}),
                }}
                // Right-click ANYWHERE on the group band opens the group menu
                // (parity with the pre-PSTUDIO-3404 merged cell, which spanned the
                // band). `openRowContextMenu` writes `data-popover-open` on
                // `tableCellRef` (the control cell), so any cell is a safe trigger.
                onContextMenu={openRowContextMenu}
              >
                {isControl && (
                  <div
                    // dnd-kit's `handle` option points at this ref — the user can
                    // only initiate a drag from this strip. The IconButton inside
                    // still receives click events via its own onClick because the
                    // sortable distinguishes drag-start from a plain tap via
                    // movement threshold.
                    ref={dragHandleRef}
                    test-id={`c-editor-table-tr-group__drag-handle-${rowIndex}`}
                    className="c-editor-table-tr-group__control"
                  >
                    <IconButton
                      test-id="c-editor-table-tr-group__toggle"
                      size="small"
                      name={
                        !rowSettings?.groupExpanded
                          ? 'chevron-down'
                          : 'chevron-up'
                      }
                      onClick={() => tableStore.toggleRowGroupVisibility(rowId)}
                    ></IconButton>
                  </div>
                )}

                {/* Anchored to the control cell (id={cellId}); kept OUT of the
                  drag-handle strip so it can never interfere with dnd-kit's
                  pointer handling. Rendered only in the control cell. */}
                {isControl &&
                  tableStore.isEditing() &&
                  contextMenuState !== 'closed' && (
                    <RowContextMenu
                      rowId={rowId}
                      triggerId={cellId}
                      rowIndex={rowIndex}
                      active={contextMenuState === 'open'}
                      onRequestClose={() => setContextMenuState('closing')}
                    />
                  )}

                {/* PSTUDIO-3865: the header content this column hosts, by
                  POSITION (number → count → name → actions across the first four
                  visible non-control columns). Each element lives in its own
                  cell; inner cells clip, the actions cell overflows right. */}
                {slots.length > 0 && (
                  <div className="c-editor-table-tr-group__slots">
                    {slots.includes('nodeNumber') && subRows?.length > 0 && (
                      <div
                        test-id="c-editor-table-tr-group__label"
                        className="c-editor-table-tr-group__item"
                      >
                        <TextBody size="small" variant="tertiary">
                          {tableStore.getValue(
                            subRows[0],
                            NodeColumns.nodeNumber
                          )}
                        </TextBody>
                      </div>
                    )}

                    {slots.includes('count') && (
                      <div className="c-editor-table-tr-group__item c-editor-table-tr-group__item--count">
                        <Stack nowrap alignItems="center" gutter="2xsmall">
                          <span
                            test-id="c-editor-table-tr-group__count"
                            className="c-editor-table-tr-group__tag"
                          >
                            {subRows?.length}
                          </span>
                          <TextBody size="small" variant="secondary">
                            Nodes
                          </TextBody>
                        </Stack>
                      </div>
                    )}

                    {slots.includes('name') && (
                      <input
                        key={`${rowId}:groupName:${
                          rowSettings?.groupName ?? ''
                        }`}
                        test-id="c-editor-table-tr-group__name"
                        type="text"
                        placeholder="Group name"
                        className="c-editor-table-tr-group__input"
                        defaultValue={rowSettings?.groupName ?? ''}
                        onBlur={(e) => {
                          commitGroupName(e.currentTarget.value ?? '');
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                            return;
                          }
                          // Arrow keys would otherwise bubble to table-nav on
                          // <table> and shift focus to neighboring focusable
                          // widgets in the group row (chevron, action buttons).
                          if (
                            e.key === 'ArrowLeft' ||
                            e.key === 'ArrowRight' ||
                            e.key === 'ArrowUp' ||
                            e.key === 'ArrowDown'
                          ) {
                            e.stopPropagation();
                          }
                        }}
                      />
                    )}

                    {slots.includes('actions') && (
                      <div className="c-editor-table-tr-group__actions">
                        {/* PSTUDIO-3866: toolbar order is
                            plus → copy → palette → unlink → delete. The color
                            (palette) button sits BETWEEN copy and ungroup. */}
                        <Tooltip content="Add node">
                          <IconButton
                            test-id="c-editor-table-tr-group__add-node"
                            name="plus"
                            size="small"
                            onClick={() => tableStore.addRowToGroup(rowId)}
                          ></IconButton>
                        </Tooltip>
                        <Tooltip content="Copy group">
                          <IconButton
                            test-id="c-editor-table-tr-group__copy"
                            name="copy"
                            size="small"
                            onClick={() => tableStore.copyRows([rowId])}
                          ></IconButton>
                        </Tooltip>
                        {/* PSTUDIO-3866: group color picker trigger, between
                            copy and ungroup. */}
                        <Tooltip
                          content={String(
                            t(`${WORKFLOWS_NS}:group_color.tooltip`)
                          )}
                        >
                          <IconButton
                            test-id="c-editor-table-tr-group__color-picker"
                            name="palette"
                            size="small"
                            ref={(el: HTMLElement | null) => {
                              colorPickerBtnRef.current = el;
                            }}
                            onClick={openColorPicker}
                          ></IconButton>
                        </Tooltip>
                        <Tooltip content="Ungroup nodes">
                          <IconButton
                            test-id="c-editor-table-tr-group__ungroup"
                            name="unlink"
                            size="small"
                            onClick={() => tableStore.ungroupRows(rowId)}
                          ></IconButton>
                        </Tooltip>
                        <Tooltip content="Delete node group">
                          <IconButton
                            test-id="c-editor-table-tr-group__delete"
                            name="delete"
                            size="small"
                            onClick={() => tableStore.deleteRowGroup(rowId)}
                            disabled={!tableStore.canDeleteRow(rowId)}
                            title={
                              tableStore.canDeleteRow(rowId)
                                ? ''
                                : 'Cannot delete last row'
                            }
                          ></IconButton>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                )}
              </td>
            );
          })}
        </tr>
        {/* PSTUDIO-3393: color-picker floating panel — portal to body so it
          escapes the virtualized table's overflow container. Position is
          computed from the trigger button's getBoundingClientRect() and fixed
          relative to the viewport. */}
        {colorPickerOpen &&
          colorPickerAnchorRect &&
          createPortal(
            // PSTUDIO-3393: TWO separate containers stacked vertically —
            // matches Figma file RhFJR0pxKC5X12geakgi2H:
            //   5139:75257 (swatch pill, radius 100px)  — always shown
            //   5139:75240 (HSV card,   radius 8px)     — shown only in customColorMode
            // A single container with radius 100px wrapped both before,
            // which produced a large white "blob" once the tall HSV picker
            // expanded inside it. They are now independent elements with their
            // own background, shadow, and radius so neither affects the other.
            <div
              ref={colorPickerPanelRef}
              className="c-group-color-picker-portal"
              style={{
                position: 'fixed',
                // Align portal below the trigger button.
                top: colorPickerAnchorRect.bottom + 8,
                left: colorPickerAnchorRect.left,
              }}
              // Prevent clicks inside the panel from bubbling to the
              // document pointerdown handler that closes it.
              onPointerDown={(e) => e.stopPropagation()}
            >
              {/* ── Swatch pill (5139:75257) ─────────────────────────────
                  Standalone capsule: radius 100px, own bg + shadow,
                  always rendered regardless of customColorMode. */}
              <div className="c-group-color-picker-panel">
                {/* Preset swatches row */}
                <div className="c-group-color-picker-panel__swatches">
                  {GROUP_COLOR_PRESETS.map((hex) => {
                    const isSelected =
                      groupColor?.toUpperCase() === hex.toUpperCase();
                    return (
                      <button
                        key={hex}
                        type="button"
                        className={[
                          'c-group-color-picker-panel__swatch',
                          isSelected
                            ? 'c-group-color-picker-panel__swatch--selected'
                            : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        style={{ backgroundColor: hex }}
                        aria-label={hex}
                        title={hex}
                        onClick={() => {
                          tableStore.setGroupColor(rowId, hex);
                          setColorPickerOpen(false);
                          setCustomColorMode(false);
                        }}
                      >
                        {isSelected && (
                          <svg
                            className="c-group-color-picker-panel__swatch-check"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M2 6L5 9L10 3"
                              // Contrast-aware checkmark: white on dark swatches,
                              // dark on light swatches (e.g. #FFA800) so it stays
                              // legible on every preset.
                              stroke={
                                getGroupRowTextTone(hex) === 'light'
                                  ? '#ffffff'
                                  : '#151a25'
                              }
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                  {/* Rainbow "Custom" swatch */}
                  <button
                    type="button"
                    className={[
                      'c-group-color-picker-panel__swatch',
                      'c-group-color-picker-panel__swatch--custom',
                      customColorMode
                        ? 'c-group-color-picker-panel__swatch--selected'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-label="Custom color"
                    title="Custom color"
                    onClick={() => setCustomColorMode((prev) => !prev)}
                  >
                    {/* PSTUDIO-3393: white checkmark shown when the active group
                        color is a custom (non-preset) color. Exact Figma vector,
                        node 5139:75283 child (M0.45 5.63 L3.12 8.45 L8.45 0.45). */}
                    {isCustomColorSelected && (
                      <svg
                        className="c-group-color-picker-panel__swatch-check"
                        viewBox="0 0 8.9 8.9"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M0.45 5.63L3.12 8.45L8.45 0.45"
                          stroke="#ffffff"
                          strokeWidth="0.9"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* ── HSV card (5139:75240) ─────────────────────────────────
                  Separate card BELOW the pill; shown only in customColorMode.
                  Figma radius = 8px (rounded-[8px] in Figma output); this is
                  intentionally NOT 100px — that was the blob bug. */}
              {customColorMode && (
                <div className="c-group-color-picker-card">
                  <ColorPicker
                    // @ts-expect-error — `inline` is a boolean Lit property;
                    // the @lit/react wrapper types it correctly but may surface
                    // as unknown when the generated TS is absent or stale.
                    inline
                    no-format-toggle
                    // PSTUDIO-3866: feed the *previewed* color back, not the
                    // committed `groupColor`. `@lit/react` re-applies every prop
                    // to the element on EVERY render with no dirty-check
                    // (create-component.js: `node[name] = value`, in a
                    // no-deps useLayoutEffect). During an HSV/hue/alpha drag,
                    // each `input` frame re-renders this row, so a controlled
                    // `value={groupColor}` (unchanged mid-drag) would be re-set
                    // onto <ps-color-picker> and its unguarded `handleValueChange`
                    // re-parses it — snapping the grid handle back to the
                    // committed color while the header (dragPreviewColor) tracked
                    // the pointer, so release then commits the stale color.
                    // `effectiveGroupColor` (= dragPreviewColor ?? groupColor)
                    // equals the value the WC just emitted, so the re-applied
                    // prop is a Lit no-op and the handle follows the pointer.
                    value={effectiveGroupColor}
                    swatches=""
                    // PSTUDIO-3393: `input` fires on every drag frame — only
                    // preview locally, never touch the store here (see
                    // dragPreviewColor). `change` fires once, on release — that
                    // is where we commit, so a drag is one atomic undo + save.
                    // @ts-expect-error — `onInput`/`onChange` event target is the
                    // WC element, not HTMLInputElement; value is on e.target per
                    // the WC impl.
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      tableStore.setGroupColorPreview(
                        rowId,
                        e.target.value || null
                      );
                    }}
                    // @ts-expect-error — see onInput above.
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const val = e.target.value;
                      tableStore.setGroupColor(rowId, val || null);
                      tableStore.setGroupColorPreview(rowId, null);
                    }}
                  />
                </div>
              )}
            </div>,
            document.body
          )}
      </>
    );
  }

  return (
    <>
      <InsertionLines rowIndex={rowIndex} />
      <tr
        ref={combinedRef}
        test-id={`c-editor-table-tr-${rowIndex}`}
        data-rowid={rowId}
        data-row-index={rowIndex}
        data-index={virtualIndex}
        data-selected={isRowSelected || undefined}
        className={classNames}
        key={rowId}
        style={(() => {
          const s: React.CSSProperties & Record<string, string> = {
            ...rowStyles,
          };
          if (parentGroupColor) {
            (s as any)['--group-guide-color'] = parentGroupColor;
          }
          return Object.keys(s).length > 0 ? s : undefined;
        })()}
      >
        {renderIndexCell()}
        {childrenWithHandles}
      </tr>
    </>
  );
};

const ForwardedDraggableRow = forwardRef(DraggableRowComponent);
export const DraggableRow = observer(ForwardedDraggableRow);
